/**
 * Mixed 3D Viewer showcase app.
 *
 * Thin shell that demonstrates how a consumer would wire up
 * @ais/viewer-core (the headless engine) with @ais/viewer-ui (optional DOM
 * widgets). For a different project that just wants the engine, drop the
 * UI imports — everything in @ais/viewer-core works without any DOM beyond
 * the canvas host.
 */
import '@ais/viewer-ui/styles.css';
import './PluginBrowser.css';
import {
  Viewer,
  VIEWER_CORE_VERSION,
  createContentAddressedUploader,
  exportSnapshot,
  healSnapshotUrls,
  classifyPly,
  streamedSiblingUrl,
  type SnapshotPluginRef,
  type SplatConvertState,

  applyAppConfig,
  exportAppConfig,
  loadAppConfig,
  type AppConfig,} from '@ais/viewer-core';
import {
  Sidebar,
  IconToolbar,
  ControlBar,
  InfoBar,
  SceneTreePanel,
  SceneBar,
  ViewportOverlay,
  LoadingOverlay,
  DropZone,
  SplitView,
  Popup,
  ResultPicker,
  createSiteAssetPicker,
} from '@ais/viewer-ui';
import { PluginHost } from '@ais/plugin-host';
import { attachFlyKeys } from './FlyKeys';
import { PluginBrowser } from './PluginBrowser';

const host = document.getElementById('app');
if (!host) throw new Error('#app element missing');

// Injected by vite.config.ts at build time (date + short git hash).
declare const __BUILD_STAMP__: string;
const BUILD_LABEL = `v${VIEWER_CORE_VERSION} · ${__BUILD_STAMP__}`;
// Also log it, so the build is verifiable from the console even if the badge
// is ever obscured.
console.info(`[admin build] ${BUILD_LABEL}`);

const app = new Viewer(host);

// /files is the per-SITE upload/cache bucket (WebDAV → the site's /nas). Its
// prefix comes from __SERVER_BASE__ = VIEWER_BASE (default '/') — the SERVER
// base, deliberately distinct from the app-asset base ADMIN_BASE (default
// '/admin/'); the two coincide only in the portable './' build. Unset → '/files'
// (origin-root, exactly today); VIEWER_BASE=./ → './files' → app-relative, so
// under the fleet the PUT/GET/HEAD lands on THIS site via /s/<token>/admin/files/.
// (/plugins IS derived from SBASE too now — see the PluginHost below. It stayed
// origin-absolute while the registry was only ever a shared trusted-zone resource;
// a data hub mirrors the entitled bundles and serves its own, which has to be
// per-site and has to survive its uplink being down.)
declare const __SERVER_BASE__: string;
const SBASE = __SERVER_BASE__.replace(/\/$/, '');
// Same prefix the uploader (below) uses — one bucket for the geodata /files
// cache and content-addressed uploads, and portable in saved snapshots.
app.filesPrefix = `${SBASE}/files`;


// ---- checkpoint helpers (used by the Sidebar snapshotStore below) ----------
/** `yyyymmdd-hhmm` in local time — the default checkpoint name suffix. */
function checkpointStamp(): string {
  const d = new Date();
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}`;
}
/**
 * Make an operator-typed checkpoint name safe for a URL path segment: drop a
 * trailing `.json` (we add our own), keep letters / digits in ANY script plus
 * `._-`, collapse everything else (path separators, quotes, spaces, symbols) to
 * `-`, and trim leading/trailing separators. Returns '' if nothing usable is
 * left — the caller falls back to the suggested default. Letters means Unicode
 * letters: an ASCII-only class silently turned a Chinese name into dashes
 * (2026-09-11). Fetch percent-encodes the segment, nginx WebDAV decodes it to
 * a UTF-8 filename, so nothing else in the chain needs to know.
 */
function sanitizeCheckpointName(raw: string): string {
  return raw
    .trim()
    .replace(/\.json$/i, '')
    .replace(/[^\p{L}\p{N}\p{M}._-]+/gu, '-')
    .replace(/^[-.]+|[-.]+$/g, '');
}

// ---- site routes -----------------------------------------------------------
// nas-index (a JSON directory listing) and nas (the loadable files) are SIBLINGS
// of the admin app dir on the site: the admin bundle is served at
// <origin><SITE_BASE>/admin/, so `../` from this page climbs to the site root
// with no origin hardcoded — it resolves at /admin/, under the fleet at
// /s/<token>/admin/, and off a bare site origin alike. Used by the uploaders
// below; the per-kind "add from site storage" picker derives the same routes
// itself (viewer-ui `createSiteAssetPicker`), which is what lets a dashboard
// cell offer the identical browse buttons.
const SITE_ROOT = new URL('../', document.baseURI); // <origin><SITE_BASE>/

// The build badge now renders as the Sidebar header's subtitle (see the
// Sidebar options below) instead of a floating chip over the viewport —
// the horizontal toolbar owns the top strip. The console.info above stays
// the always-available fallback.

// Persist user-dropped files to /files/<hash>-<name> so saved snapshots
// can re-fetch them later (the dashboard, on a different page, has no
// access to blob: URLs created in this tab). nginx exposes /files/ as a
// WebDAV PUT target. The uploader is content-addressed: it HEAD-probes
// the hashed path first and skips the PUT when the same bytes are
// already on the server, so re-dropping a model is instant and the
// bucket doesn't accumulate duplicates.
const caUploader = createContentAddressedUploader({
  prefix: `${SBASE}/files`,
  onStatus: (msg) => app.setStatus(msg),
});
// Site builds route uploads through the DATAHUB INGRESS (below) so dropped
// files land organized on the NAS; the content-addressed store remains the
// runtime FALLBACK (and the whole path is SBASE-gated: default builds — the
// reference deploy — keep the content-addressed uploader untouched).
app.setFileUploader(SBASE ? siteIngressUploader(caUploader) : caUploader);
// Streamed splats: a splat that opens as a single file asks the hub to build
// its `-lod.rad` (Spark's streamed LoD) and switches to it when it lands. Hub
// builds only (SBASE gate) — standalone wires nothing, so a splat there loads
// as a single file and no conversion request is ever made.
if (SBASE) app.setSplatConverter(convertSplatViaHub);

/**
 * Idea-2 uploader: pick/drag → the site's ingress → organized NAS → the layer
 * loads from the ORGANIZED URL (which is also what snapshots then reference).
 *  - media (ifc/jpg/ply/…): POST api/uploads/file → file_router sorts it into
 *    /nas/media/<ext>/ within seconds → short HEAD-poll → return that URL.
 *  - las/laz: the raw bytes go to the content-addressed store as today (the
 *    loader parses them NOW; snapshots stay valid), PLUS a copy to
 *    api/uploads/las for las_to_potree; a background poll auto-adds the
 *    converted octree to the scene when it appears (minutes).
 *  - ANY failure (401/timeout/network) → the content-addressed fallback: an
 *    upload never breaks, worst case it lands the old way.
 */
function siteIngressUploader(fallback: (f: File) => Promise<string>) {
  return async (file: File): Promise<string> => {
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    try {
      if (ext === 'las' || ext === 'laz') {
        const url = await fallback(file); // stable raw copy — loader + snapshot
        void convertLasViaIngress(file);  // additive: conversion + auto-add
        return url;
      }
      // `.ply` is three different things and the hub routes each somewhere
      // different, so the URL we should wait for differs too. Guessing wrong
      // means polling a path that never appears — a 22 s stall before the
      // fallback kicks in.
      if (ext === 'ply') {
        const kind = await classifyPly(file);
        // 3DGS → compressed to /nas/media/spz/, raw archived OFF the served
        // tree. Return the .spz: ~12x fewer bytes and the checkpoint records
        // the small URL.
        if (kind === 'splat') return await uploadSplatViaIngress(file);
        // Point cloud → claimed by las_to_potree and turned into an octree, so
        // media/ply/ never appears for it. Same shape as las/laz above: hand
        // back a stable raw copy NOW, convert in the background, auto-add the
        // octree when it's ready.
        if (kind === 'cloud') {
          const url = await fallback(file);
          void convertCloudPlyViaIngress(file);
          return url;
        }
        // 'mesh' / 'unknown' → file_router files it under media/ply/, so the
        // normal media path below is correct.
      }
      const fd = new FormData();
      fd.append('file', file, file.name);
      const r = await fetch(new URL('api/uploads/file', SITE_ROOT).href, { method: 'POST', body: fd });
      if (!r.ok) throw new Error(`ingress HTTP ${r.status}`);
      const safe = ((await r.json()) as { filename?: string }).filename || file.name;
      const target = new URL(`nas/media/${ext}/${encodeURIComponent(safe)}`, SITE_ROOT).href;
      app.setStatus(`uploaded ${safe} — waiting for the file router…`);
      if (await pollUrl(target, 1500, 15)) {
        app.setStatus(`${safe} landed in site storage`);
        return target; // the ORGANIZED, picker-visible, snapshot-stable URL
      }
      throw new Error('file_router did not land the file in time');
    } catch (e) {
      console.warn(`[ingress] ${String(e)} — falling back to the content-addressed store`);
      return fallback(file);
    }
  };
}

/**
 * Send a point-cloud PLY to las_to_potree and AUTO-ADD the octree when it's
 * converted — the same background contract as convertLasViaIngress, because the
 * hub treats a cloud PLY exactly like a LAS (ply2las.py bridges it, since
 * PotreeConverter's own PLY reader aborts).
 */
async function convertCloudPlyViaIngress(file: File): Promise<void> {
  try {
    const fd = new FormData();
    fd.append('file', file, file.name);
    const r = await fetch(new URL('api/uploads/las', SITE_ROOT).href, { method: 'POST', body: fd });
    if (!r.ok) throw new Error(`ply ingress HTTP ${r.status}`);
    const safe = ((await r.json()) as { filename?: string }).filename || file.name;
    const stem = safe.replace(/\.ply$/i, '');
    const metaUrl = new URL(`nas/potree/${encodeURIComponent(stem)}/metadata.json`, SITE_ROOT).href;
    app.setStatus(`${safe}: converting to Potree octree in the background…`);
    if (await pollUrl(metaUrl, 10_000, 180)) {
      app.setStatus(`${stem}: octree ready — added from site storage`);
      await sidebar.loadAssetFromUrl('pointcloud', metaUrl);
    } else {
      console.warn(`[ingress] ${stem}: conversion not detected within 30 min (it may still finish)`);
    }
  } catch (e) {
    console.warn(`[ingress] cloud-ply conversion not started: ${String(e)}`);
  }
}

/**
 * Upload a 3DGS .ply through the dedicated splat door and wait for the hub to
 * publish it, returning the URL of the scene to load (the PLY, streamed from
 * its .rad, on a hub in "rad" mode; the .spz on an older hub).
 *
 * The backend answers immediately with `splat_url` (where the .spz WILL be) and
 * triggers `splat_to_spz`; conversion is seconds for a few hundred MB, but the
 * 1-min safety-net sweep can add up to a minute, so the poll is generous.
 * Throwing on timeout hands control back to siteIngressUploader's catch, which
 * falls back to the content-addressed store — an upload never fails outright.
 */
async function uploadSplatViaIngress(file: File): Promise<string> {
  const fd = new FormData();
  fd.append('file', file, file.name);
  app.setStatus(`uploading ${file.name} — compressing on the hub…`);
  const r = await fetch(new URL('api/uploads/splat', SITE_ROOT).href, { method: 'POST', body: fd });
  if (!r.ok) throw new Error(`splat ingress HTTP ${r.status}`);
  const body = (await r.json()) as { filename?: string; splat_url?: string; rad_url?: string };
  const safe = body.filename || file.name;
  const stem = safe.replace(/\.ply$/i, '');
  // Prefer the server's own answer; derive it only if an older hub omits it.
  const target = new URL(
    (body.splat_url || `/nas/media/spz/${stem}.spz`).replace(/^\//, ''),
    SITE_ROOT,
  ).href;
  // A hub in "rad" mode (the default since 2026-09-28) publishes the PLY
  // itself and builds <stem>-lod.rad straight from it — no .spz. Wait for the
  // .rad (written last, so present = complete; a big scene takes minutes), then
  // hand back the PLY: the engine opens it and streams from the .rad beside it.
  // If the .rad is slow, the PLY is loaded anyway and the converter hook swaps
  // in the streamed version when it lands.
  if (body.rad_url) {
    const rad = new URL(body.rad_url.replace(/^\//, ''), SITE_ROOT).href;
    app.setStatus(`${safe}: building its streamed version on the hub…`);
    if (await pollUrl(rad, 5000, 240)) {
      app.setStatus(`${stem}: streamed version ready — loading`);
      return target;
    }
    if (await pollUrl(target, 5000, 1)) return target;
    throw new Error('splat publishing did not finish in time');
  }
  app.setStatus(`${safe}: compressing to SPZ on the hub…`);
  if (await pollUrl(target, 5000, 60)) {
    app.setStatus(`${stem}.spz ready — loading the compressed scene`);
    return target;
  }
  throw new Error('splat compression did not finish in time');
}

/**
 * The hub side of `Viewer.splatConverter`: ask the site backend to build a
 * splat's streamed version (`splat_to_rad` → `<base>-lod.rad` + pages, beside
 * the source) and resolve with the `.rad` URL once it is built.
 *
 *   POST api/splats/convert {path}  → 202 {job, state, rad, url} (200 when done)
 *   GET  api/splats/convert?path=   → {state: none|queued|running|done|failed, rad?}
 *
 * `path` is the source URL as loaded — the backend takes a NAS path, a site URL
 * or a page-relative ref, and refuses (4xx) anything that is not a splat under
 * /nas/media or /nas/viewer/files: then there is simply no streamed version.
 * The `.rad` is opened through the SAME door as its source (it sits beside
 * it), so its URL is the source's sibling, not re-derived from NAS paths. The
 * build is minutes of CPU for a big scene; poll every 5 s, give up after 45
 * min (the 5-min sweep still finishes it — the next open streams).
 */
async function convertSplatViaHub(
  sourceUrl: string,
  onState?: (state: SplatConvertState) => void,
): Promise<string | null> {
  const abs = new URL(sourceUrl, document.baseURI).href;
  const rad = streamedSiblingUrl(abs);
  if (!rad) return null;
  const api = new URL('api/splats/convert', SITE_ROOT).href;
  type Answer = { state?: string; reason?: string };
  const read = async (r: Response): Promise<Answer> => { try { return (await r.json()) as Answer; } catch { return {}; } };
  try {
    const r = await fetch(api, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ path: abs }),
    });
    if (!r.ok) {
      console.info(`[splat] no streamed version for ${abs}: HTTP ${r.status}`);
      return null;
    }
    let st = await read(r);
    let idle = 0;
    for (let i = 0; i < 540; i++) {
      if (st.state === 'done') return rad;
      if (st.state === 'failed') {
        onState?.('failed');
        console.warn(`[splat] streamed build failed for ${abs}: ${st.reason ?? ''}`);
        return null;
      }
      if (st.state === 'queued' || st.state === 'running') {
        onState?.(st.state);
        idle = 0;
      } else if (++idle > 12) {
        return null; // 'none' for a minute: nothing is building it
      }
      await new Promise((res) => setTimeout(res, 5000));
      const q = await fetch(`${api}?path=${encodeURIComponent(abs)}`, { cache: 'no-store' });
      st = q.ok ? await read(q) : {};
    }
  } catch (e) {
    console.warn(`[splat] streamed build request failed: ${String(e)}`);
  }
  return null;
}

/** HEAD-poll a URL until 200 (interval ms × tries); true when it appears. */
async function pollUrl(url: string, interval: number, tries: number): Promise<boolean> {
  for (let i = 0; i < tries; i++) {
    try {
      const r = await fetch(url, { method: 'HEAD', cache: 'no-store' });
      if (r.ok) return true;
    } catch {
      /* keep polling */
    }
    await new Promise((res) => setTimeout(res, interval));
  }
  return false;
}

/** Send a LAS/LAZ copy to las_to_potree and AUTO-ADD the octree when converted. */
async function convertLasViaIngress(file: File): Promise<void> {
  try {
    const fd = new FormData();
    fd.append('file', file, file.name);
    const r = await fetch(new URL('api/uploads/las', SITE_ROOT).href, { method: 'POST', body: fd });
    if (!r.ok) throw new Error(`las ingress HTTP ${r.status}`);
    const safe = ((await r.json()) as { filename?: string }).filename || file.name;
    const stem = safe.replace(/\.(las|laz)$/i, '');
    const metaUrl = new URL(`nas/potree/${encodeURIComponent(stem)}/metadata.json`, SITE_ROOT).href;
    app.setStatus(`${safe}: converting to Potree octree in the background…`);
    // Poll every 10s for up to 30 min — conversion time scales with the scan.
    if (await pollUrl(metaUrl, 10_000, 180)) {
      app.setStatus(`${stem}: octree ready — added from site storage`);
      await sidebar.loadAssetFromUrl('pointcloud', metaUrl);
    } else {
      console.warn(`[ingress] ${stem}: conversion not detected within 30 min (it may still finish — pick it from site storage later)`);
    }
  } catch (e) {
    console.warn(`[ingress] LAS conversion send failed: ${String(e)} (raw copy still loaded + saved)`);
  }
}

/**
 * Upload a dropped POTREE FOLDER intact to /nas/potree/<name>/ via the site
 * ingress, and return the metadata URL to load from. A Potree dataset is
 * multi-file (metadata.json + octree.bin + hierarchy.bin); the generic file
 * door files by extension and would scatter it (metadata.json alone lands in
 * /nas/media/json/, orphaned from its octree). The whole-folder door keeps the
 * dataset together, so a saved snapshot references a real /nas/potree/<name>/.
 * Only wired on site builds (SBASE gate) — standalone keeps in-browser loading.
 */
async function uploadPotreeFolder(files: File[]): Promise<string | null> {
  const relOf = (f: File) =>
    (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name;
  // Dataset name = the descriptor's immediate parent folder (e.g. "stadium").
  const firstDir = files.map(relOf).find((r) => r.includes('/'));
  const parts = firstDir ? firstDir.split('/') : [];
  const name = parts.length >= 2 ? parts[parts.length - 2] : 'potree';
  const fd = new FormData();
  fd.append('name', name);
  // Flat upload: Potree v2 datasets are a flat folder, and the backend basenames
  // each leaf — send basenames so nested drops still land side by side.
  for (const f of files) fd.append('files', f, relOf(f).split('/').pop() || f.name);
  app.setStatus(`uploading potree "${name}" to site storage…`);
  const r = await fetch(new URL('api/uploads/potree', SITE_ROOT).href, { method: 'POST', body: fd });
  if (!r.ok) throw new Error(`potree ingress HTTP ${r.status}`);
  const j = (await r.json()) as { name?: string; metadata?: string };
  if (!j.metadata) throw new Error('no metadata path returned');
  const url = new URL(j.metadata, SITE_ROOT).href;
  app.setStatus(`potree "${j.name ?? name}" stored — loading from site storage`);
  return url;
}

// Plugin marketplace host — constructed before the Sidebar so its
// `installForSnapshot` can be wired into the snapshot-load hook below.
// PluginHost is cheap to construct; `restoreInstalled` is the async bit
// and runs later.
// WHICH APP this visit is, if any. The launcher opens an app as
// `?app-cfg=…&plugin=<the app's manifest>&plugin=<each dependency>`, so the FIRST
// plugin param names the app; its id is the last path segment before the version.
// Both empty on a plain editor visit. The manifest URL is the app's identity in a
// scene saved here (`snapshot.app`, resolved relative to this page so a relative
// link records an absolute one a dashboard cell can fetch).
const { APP_ID, APP_MANIFEST_URL } = (() => {
  const first = new URLSearchParams(window.location.search).getAll('plugin')[0];
  if (!first) return { APP_ID: '', APP_MANIFEST_URL: '' };
  const parts = first.split('/').filter(Boolean);
  const i = parts.lastIndexOf('plugins');
  if (i < 0 || !parts[i + 1]) return { APP_ID: '', APP_MANIFEST_URL: '' };
  let href = first;
  try { href = new URL(first, window.location.href).href; } catch { /* keep as given */ }
  return { APP_ID: parts[i + 1], APP_MANIFEST_URL: href };
})();

const pluginHost = new PluginHost(app, {
  engineVersion: VIEWER_CORE_VERSION,
  // The install set is SCOPED on a site build, because there "open an app" must
  // mean a workspace, not an addition to a pile. One key for the whole mount made
  // every app show every plugin ever installed in that browser — a cfg then looked
  // ignored, and the app's own declaration meant nothing.
  //
  //   standalone            one cumulative key. Extensions is available there and
  //                         the operator's set is theirs to keep — unchanged.
  //   site build + an app   a key per app, so one app's installs never land in
  //                         another's. Written, never restored (below): the
  //                         link names the whole set on every visit.
  //   site build, no app    the plain editor. Its set is never restored (below),
  //                         so it is the engine's own tools and no leftovers.
  //
  // A dashboard cell already worked this way (a per-cell key, no restore); this is
  // the editor catching up.
  storageKey: SBASE && APP_ID
    ? `@ais/plugin-host:admin:${APP_ID}`
    : '@ais/plugin-host:admin',
  // WHERE the registry lives depends on who is hosting us, so it follows the same
  // SBASE seam as filesPrefix rather than being hardcoded:
  //
  //   standalone (VIEWER_BASE unset -> SBASE '')  the default
  //     '/plugins/registry.json' — the SHARED marketplace at the origin root.
  //
  //   site build (VIEWER_BASE set)                `${SBASE}/plugins/registry.json`
  //     the HOST's own mirror. A data hub fetches the entitled bundles from the
  //     master store once and serves them itself, so the marketplace host is not
  //     in this page's import graph at run time: the site keeps working with its
  //     uplink down, which is the entire reason the mirror exists. It also has to
  //     be per-site — one origin-root path cannot answer for every site behind a
  //     shared front, and entitlement differs per site.
  //
  // (The earlier comment here asserted the opposite — that the registry is always
  // a shared trusted-zone resource behind a DMZ `location /plugins/`. That holds
  // for a standalone deployment, which is why it stays the default, but it cannot
  // serve a data hub: same URL, every site, no offline story.)
  ...(SBASE ? { registryUrl: `${SBASE}/plugins/registry.json` } : {}),
});

// ---- checkpoint store helpers (shared by save / saveTo / pick / pickRef) ----
/** Append the '@ais/registry' marker (current registry endpoint) so loading a
 *  checkpoint anywhere re-points the PluginHost before resolving plugin marks.
 *  Idempotent (drops any prior marker); the engine ignores it (applySnapshot
 *  reads only `extensions`). */
function stampRegistryMark(snap: object): void {
  const o = snap as { plugins?: Array<Record<string, unknown>> };
  const marks = (Array.isArray(o.plugins) ? o.plugins : []).filter((m) => m.id !== '@ais/registry');
  marks.push({ id: '@ais/registry', registryUrl: pluginHost.getRegistryUrl() });
  o.plugins = marks;
}
/** PUT a snapshot to a checkpoint URL (named path, overwritten in place). */
async function putCheckpoint(url: string, snap: object): Promise<void> {
  stampRegistryMark(snap);
  const res = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(snap, null, 2),
  });
  if (!res.ok) throw new Error(`checkpoint PUT ${url} → HTTP ${res.status}`);
}
async function fetchCheckpoint(url: string): Promise<object> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`checkpoint fetch ${url} → HTTP ${res.status}`);
  return (await res.json()) as object;
}
function pickCheckpointUrl(): Promise<string | null> {
  return ResultPicker(`${SBASE}/files/checkpoints/`, {
    title: 'Load checkpoint',
    filter: (n) => n.toLowerCase().endsWith('.json'),
  });
}

// Mount the optional UI widgets. The keys ('sidebar', 'splitView', ...)
// let widgets find each other via `viewer.ui[key]` (e.g. DropZone calls
// the Sidebar to register newly-loaded assets). The engine itself never
// reads the registry.
// Plugin SERIALIZATION MARK resolver — one copy, used by the Sidebar's saves
// AND the headless-recalc autosave, so the two can never record different refs.
const pluginRefsForSnapshot = (id: string) => {
  const r = pluginHost.listInstalled().find((p) => p.id === id);
  return r ? { version: r.version, manifestUrl: r.manifestUrl } : undefined;
};

const sidebar = new Sidebar(app, {
    // SECTIONS — deliberately NO addSections / hiddenSections here: the
    // standalone admin (cccai) is the full authoring surface and shows ALL
    // built-in sections + every installed plugin panel. Minimal downstream
    // hosts (datahub embeds, kiosks) opt down via addSections; a loaded
    // checkpoint's ui.sidebar.sections still overrides at apply time.
    // Product wordmark in place of the icon + title (text until the logo is
    // confirmed by UI/UX).
    brand: 'FieldHub',
    // Build badge — under the wordmark, so the live bundle is confirmable
    // at a glance after a deploy (hard-refresh if stale).
    // The visible line drops the DATE (the ~120px title column would
    // ellipsize the tail otherwise, hiding exactly the bits that change
    // every deploy — time + commit); the tooltip carries the full stamp.
    subtitle: `v${VIEWER_CORE_VERSION} ${__BUILD_STAMP__.replace(/^\d{4}-\d{2}-\d{2} /, '')}`,
    subtitleTip: `${BUILD_LABEL} — build time (Malaysia) / commit. Bumps every deploy.`,
    // Auto-install any plugins the snapshot relies on before applySnapshot
    // runs — mirrors the dashboard's viewer-snapshot widget path. Without
    // this, a snapshot that mentions e.g. `slope.tool.v1` would skip its
    // applyState (plugin not yet installed) and leave any stale plugin visuals
    // on screen. Passes the serialization MARK (`snapshot.plugins`) alongside
    // the extensions STATE so the host prefers each plugin's recorded
    // manifestUrl and falls back to a registry id-lookup. Absent mark ⇒
    // undefined ⇒ byte-identical to the pre-mark install path.
    beforeApplySnapshot: async (json) => {
      json = healSnapshotUrls(json);   // §15 — marks must resolve through any front
      const obj = json && typeof json === 'object' ? (json as Record<string, unknown>) : undefined;
      const exts = obj?.extensions as Record<string, unknown> | undefined;
      let marks = Array.isArray(obj?.plugins)
        ? (obj!.plugins as SnapshotPluginRef[])
        : undefined;
      // The '@ais/registry' MARKER (appended by snapshotStore.save) says which
      // store the plugins came from: re-point the host + persist the choice
      // (same per-mount key the App-store UI uses), then STRIP it so it is
      // never resolved as a plugin id.
      const regMark = marks?.find((m) => m.id === '@ais/registry' && m.registryUrl);
      // A SITE build does not follow that marker. Its host mirrors the entitled
      // add-ons and serves its own registry precisely so it keeps working when
      // the store is unreachable; letting a checkpoint re-point the host at the
      // store undoes that — and worse, the choice is PERSISTED per mount, so one
      // checkpoint authored elsewhere silently moves every later install onto a
      // remote origin. On a site build the local mirror wins; the marker is still
      // stripped, and each plugin's own recorded manifestUrl still resolves.
      if (regMark?.registryUrl && SBASE) {
        if (new URL(regMark.registryUrl, window.location.href).origin !== window.location.origin) {
          console.info(
            `[plugin] ignoring the checkpoint's registry marker (${regMark.registryUrl}) — `
            + `this site serves its own mirror at ${pluginHost.getRegistryUrl()}`,
          );
        }
      } else if (regMark?.registryUrl) {
        pluginHost.setRegistryUrl(regMark.registryUrl);
        try {
          localStorage.setItem(
            `@ais/plugin-host:registry-url:${location.pathname}`,
            regMark.registryUrl,
          );
        } catch {
          /* best-effort persistence */
        }
      }
      marks = marks?.filter((m) => m.id !== '@ais/registry');
      if (marks && marks.length === 0) marks = undefined;
      // An APP door is a workspace: the scene's marks pin versions, they do not
      // widen the set. The link already installed the app + its dependencies;
      // this re-asserts exactly that (idempotent), so a checkpoint the full
      // editor stamped with every plugin it had does not drag them all in here
      // — that pile is what made a Model Comparison door show 17 panels.
      // A plain editor (no app) still installs whatever the scene marks.
      if (APP_ID) {
        const r = await pluginHost.installApp({ id: APP_ID, manifestUrl: APP_MANIFEST_URL }, marks);
        if (r.missing.length > 0) {
          console.warn(`[admin] app ${APP_ID} needs unknown/incompatible plugins: ${r.missing.join(', ')}`);
        }
        return;
      }
      if ((!exts || typeof exts !== 'object') && !marks) return;
      // The full editor on a hub does not install APPS from a scene either: a
      // file the old full editor saved marks every app it had (li-gs included),
      // and following that mark reshaped this editor on load. Their state stays
      // in the JSON untouched (§6); it just isn't applied here.
      const apps = SBASE ? await registryAppIds() : new Set<string>();
      let extsToInstall = exts && typeof exts === 'object' ? exts : {};
      if (apps.size > 0) {
        marks = marks?.filter((m) => !apps.has(m.id));
        extsToInstall = Object.fromEntries(Object.entries(extsToInstall).filter(([id]) => !apps.has(id)));
      }
      const result = await pluginHost.installForSnapshot(extsToInstall, marks);
      if (result.installed.length > 0) {
        console.info(`[admin] auto-installed plugins from snapshot: ${result.installed.join(', ')}`);
      }
      if (result.missing.length > 0) {
        console.warn(`[admin] snapshot references unknown/incompatible plugins: ${result.missing.join(', ')}`);
      }
    },
    // Redirect Save / Load away from browser download / file-picker and onto
    // named checkpoints under this site's /files bucket (WebDAV PUT + autoindex
    // JSON listing via nginx). All URLs derive from SBASE so they land on THIS
    // site under the fleet (/s/<token>/admin/files/checkpoints/…).
    // GATED on a base-portable build (VIEWER_BASE set ⇒ SBASE non-empty): the
    // DEFAULT build keeps today's Save-downloads / Load-file-picker behavior —
    // the fallback contract of this branch. (Spread of false adds nothing.)
    ...(SBASE && {
    // Site deployment (the datahub's Editor tab): the drawer starts CLOSED — the
    // 3D scene is the point; the chevron re-opens it. Default builds stay open
    // (the fallback contract, like everything else in this gate). The header
    // itself is no longer set here: viewer-ui's default is 'Scene', which is the
    // same word in every host, so there is nothing left to differ.
    defaultCollapsed: true,
    snapshotStore: {
      // Save As: prompt for a NEW name and PUT the JSON to that named checkpoint
      // (NOT the content-addressed uploader — checkpoints are named, human-chosen,
      // overwritten in place). Return its URL so the Sidebar can then offer a
      // plain "Save" that overwrites this same checkpoint.
      async save(snap) {
        const suggested = `checkpoint-${checkpointStamp()}`;
        const raw = window.prompt('Save scene checkpoint as:', suggested);
        if (raw === null) throw new Error('save cancelled');
        const name = sanitizeCheckpointName(raw) || suggested;
        const url = `${SBASE}/files/checkpoints/${name}.json`;
        await putCheckpoint(url, snap);
        return url;
      },
      // Save: overwrite the loaded checkpoint (its URL) in place, no prompt.
      async saveTo(snap, ref) {
        await putCheckpoint(ref, snap);
      },
      // Load (snapshot only) — kept for the SnapshotStore contract.
      async pick() {
        const url = await pickCheckpointUrl();
        return url ? await fetchCheckpoint(url) : null;
      },
      // Load (snapshot + its URL) — arms "Save" to overwrite what you loaded.
      async pickRef() {
        const url = await pickCheckpointUrl();
        return url ? { snapshot: await fetchCheckpoint(url), ref: url } : null;
      },
    },
    // Per-type "add from site storage": each scene section grows a browse
    // button that lists the site NAS (nas-index JSON) and loads the picked
    // /nas/ URL through the layer's normal loader. Same SBASE gate as the
    // checkpoint store — the DEFAULT build shows only the local-file "+".
    assetPicker: createSiteAssetPicker(),
    // Plugin SERIALIZATION MARK source: Save records each installed plugin's
    // { version, manifestUrl } so a checkpoint saved here re-resolves its
    // plugins from the marketplace when loaded on another site. Gated with the
    // checkpoint store — the DEFAULT build writes no `plugins` key at all.
    pluginRefs: pluginRefsForSnapshot,
    // WHICH APP a scene saved here belongs to (`snapshot.app`). An app door
    // stamps itself — the installed record carries the exact version, the link
    // is the fallback before the first install lands. The plain editor returns
    // undefined and the Sidebar carries a loaded file's `app` through a re-save,
    // so editing a Model Comparison scene in the full editor keeps it one.
    app: () => APP_ID
      ? { id: APP_ID, ...(pluginRefsForSnapshot(APP_ID) ?? { manifestUrl: APP_MANIFEST_URL }) }
      : undefined,
    }),
  });
app.attachUI(sidebar, 'sidebar');
app.attachUI(new IconToolbar(app), 'iconbar');
app.attachUI(new ControlBar(app), 'controlbar');
app.attachUI(new InfoBar(app), 'infobar');
app.attachUI(new SceneTreePanel(app), 'sceneTree');
app.attachUI(new SceneBar(app), 'scenebar');
app.attachUI(new ViewportOverlay(app), 'overlay');
app.attachUI(new LoadingOverlay(app), 'loadingOverlay');
app.attachUI(new DropZone(app, SBASE ? { potreeUploader: uploadPotreeFolder } : {}), 'dropzone');
app.attachUI(new SplitView(app), 'splitView');
// The one info card over the viewport (viewer.ui.popup) — invisible until a
// tool or plugin opens one; single instance, so a new card replaces the last.
app.attachUI(new Popup(app), 'popup');

// Wire the existing #status div to the engine's status stream.
const statusEl = document.getElementById('status');
if (statusEl) {
  app.onStatus((msg) => {
    statusEl.textContent = msg;
  });
}

app.start();

// Plugin marketplace. Restore previously-installed plugins from
// localStorage *before* any user action / snapshot apply, so plugins that
// own state in snapshot.extensions[] re-apply cleanly on reload. The
// browser UI is itself a ViewerPlugin and uses Sidebar.addPanel just like
// any third-party plugin would. Marketplace requires the production build
// (importmap is injected at build time only — see vite.config.ts).
// Registry controls only on site builds (SBASE gate — the fallback contract):
// operators can re-point extension fetching (e.g. at the trusted-zone store)
// and open its marketplace; default builds keep the fixed-registry panel.
const pluginBrowser = new PluginBrowser(pluginHost, {
  registryControls: !!SBASE,
  // A site build resolves add-ons from its host's mirror; siteBuild makes the
  // panel discard a SAVED cross-origin registry rather than re-applying it every
  // reload (see PluginBrowser's constructor for why that matters).
  siteBuild: !!SBASE,
});
void app.use(pluginBrowser);
// A SITE build does not offer "browse and install more plugins" at all. On a data
// hub the add-on set is decided upstream and managed on the hub's own Add-on page:
// what is installable here is only ever what that hub already mirrored, so the
// panel adds no capability — it just puts a second, contradictory place to manage
// add-ons inside the tool that consumes them. Standalone keeps it; that difference
// IS the hub build.
//
// Hidden via the deny-list, so nothing else is touched, and the browser stays
// INSTALLED and working — a cfg that names the panel still gets it (below), and
// programmatic installs (?plugin=, snapshot marks) are unaffected.
if (SBASE) {
  (app.ui.sidebar as { setSectionHidden?: (k: string, h: boolean) => void } | undefined)
    ?.setSectionHidden?.(pluginBrowser.id, true);
}
// Standalone: restore the operator's own set (Extensions is offered there).
//
// An APP on a site build restores NOTHING: its link names the app and every
// dependency, and that is the whole set — there is no Extensions panel to have
// added more. Restoring the per-app key would resurrect whatever a scene's marks
// once installed into it (the full editor's 17-plugin pile, before app doors
// stopped following marks) on every later visit, with no UI to clean it up.
// The headless recalc runner (datahub `checkpoint_recalc` flow, gated again where
// it saves, below) is the one full-editor visit that must hold EVERY plugin, apps
// included: its job is to replay a scene's numbers, and a slope scene's numbers
// come from an app. Nobody looks at its sidebar.
const RECALC_RUNNER = !!SBASE && new URLSearchParams(window.location.search).get('recalc-autosave') === '1';

/** Ids the hub's registry classes as APPS (`type: 'app'`) — the plugins the full
 *  editor leaves to their doors. Empty on standalone, for the recalc runner, and
 *  when the registry is unreachable. */
async function registryAppIds(): Promise<Set<string>> {
  if (!SBASE || RECALC_RUNNER) return new Set();
  try {
    return new Set((await pluginHost.listAvailable()).filter((e) => e.type === 'app').map((e) => e.id));
  } catch {
    return new Set();
  }
}

// Awaited by the `?checkpoint=` deep link below, like linkPlugins: the full editor's
// toolset must be in place before a scene applies its plugins' state.
let bootPlugins: Promise<void> = Promise.resolve();
if (!SBASE) {
  void pluginHost.restoreInstalled();
} else if (!APP_ID) {
  // The PLAIN editor on a site build is the hub's whole toolset: every viewer plugin
  // the hub is entitled to and has mirrored — which is exactly what its Add-on page
  // lists. Read from the REGISTRY rather than the stored set, because the registry is
  // the current answer: a de-entitled plugin drops out on its own, where a restored
  // key would keep resurrecting it (and there is no Extensions panel on a hub to
  // clean up by hand).
  //
  // dashboard-widget entries are skipped — those are cell types, not viewer plugins,
  // and installing one here would fail its adapter check for no reason.
  //
  // APPS (`type: 'app'`) are skipped too: an app is a workspace with a door of its
  // own on the hub's launcher, not a tool to add to the full editor — and one of
  // them reshapes the editor on install (li-gs narrows the sidebar to Splats and
  // says "uninstall to restore"), which made every plain-editor visit on a hub open
  // as the Li-GS station with no way back (live 2026-09-03). The recalc runner
  // keeps them (RECALC_RUNNER above).
  bootPlugins = (async () => {
    let entries: Awaited<ReturnType<typeof pluginHost.listAvailable>>;
    try {
      entries = await pluginHost.listAvailable();
    } catch (e) {
      console.warn(`[plugin] registry unreachable, full editor starts empty: ${(e as Error).message}`);
      return;
    }
    const wanted = entries.filter(
      (e) => (e.kind ?? 'viewer-plugin') !== 'dashboard-widget' && (RECALC_RUNNER || e.type !== 'app'),
    );
    let ok = 0;
    // Sequential: installs share the host's install set and its persisted copy, and
    // one plugin failing its engine check must not cost the others.
    for (const entry of wanted) {
      try {
        await pluginHost.install(entry);
        ok += 1;
      } catch (e) {
        console.warn(`[plugin] skipped ${entry.id}: ${(e as Error).message}`);
      }
    }
    console.info(`[plugin] full editor: installed ${ok}/${wanted.length} viewer plugin(s) from this hub`);
  })();
}

// ---------------------------------------------------------------------------
// App configuration (`app-cfg.json`)
//
// How this DEPLOYMENT is set up — which sidebar sections exist, which bars
// start collapsed — as opposed to a snapshot, which is a scene. Read once at
// boot from `?app-cfg=<url>` or `app-cfg.json` beside the app; absent is the
// normal case and stays silent. A snapshot loaded later may override it: the
// user's file beats the deployment default, by time rather than by rank.
//
// `window.exportAppConfig()` prints (and downloads) the CURRENT bar setup as a
// cfg — set the app up by hand, run it, ship the file.
// ---------------------------------------------------------------------------
void (async () => {
  const cfg = await loadAppConfig(import.meta.env.BASE_URL ?? './');
  if (!cfg) return;
  const applied = applyAppConfig(app, cfg);
  console.info(`[app-cfg] ${cfg.label ? `"${cfg.label}" ` : ''}applied to: ${applied.join(', ') || '(nothing mounted)'}`);

  // A cfg means this is a CONFIGURED admin — a deployment set up for a job, or
  // the store's review link — not the open editor. Browsing and installing more
  // plugins is a standalone-editor act, so the Extensions panel goes away: it
  // would otherwise invite changing the very thing the cfg just pinned. A cfg
  // that names the panel in its sections is asking for it explicitly and keeps
  // it. Hiding is exact (the deny-list), so nothing else in the cfg is touched,
  // and the plugin browser stays INSTALLED and working — the panel is the only
  // thing withheld, per §10.
  const sections = (cfg.ui?.sidebar as { sections?: string[] } | undefined)?.sections;
  const named = !!sections?.includes(pluginBrowser.id);
  const sidebar = app.ui.sidebar as
    { setSectionHidden?: (k: string, h: boolean) => void } | undefined;
  if (SBASE) {
    // A site build hid the panel up front, so the escape hatch has to work in
    // BOTH directions: a cfg that names the panel is asking for it explicitly.
    sidebar?.setSectionHidden?.(pluginBrowser.id, !named);
  } else if (!named) {
    // Standalone keeps its original rule EXACTLY — hide only when the cfg does
    // not name the panel, and otherwise touch nothing. Un-hiding here would
    // override a deny someone else set (a snapshot's hiddenSections), which is a
    // change no standalone user asked for.
    sidebar?.setSectionHidden?.(pluginBrowser.id, true);
  }
})();

// ---------------------------------------------------------------------------
// `?plugin=<manifestUrl>` — review deep-link
//
// The store's "Open in editor" hands over a plugin's cfg, which sets the SURFACE
// it asked for. On its own that is half a review: the cfg does not install
// anything, so an editor without the plugin already installed shows the declared
// sections and no plugin — which reads as "my panel disappeared" rather than
// "nothing loaded it". This installs the exact version being reviewed.
//
// Installing a module named in a QUERY PARAM is remote code execution by link,
// so the URL must belong to this site's own store: same origin as the page, or
// the origin the registry is already pointed at (a site build may legitimately
// fetch from the trusted-zone store). Anything else is refused out loud — a
// crafted link is exactly the shape this guard exists for.
// ---------------------------------------------------------------------------
// Awaited by the `?checkpoint=` deep link below: a scene must open into an app
// that is already there, not race its install (two installs of one id — the
// link's and the scene's — and the loser throws "already installed").
const linkPlugins = (async () => {
  // getAll, not get: an app can name the add-ons it DEPENDS on, and a host that
  // installs only the first opens it missing half of itself (model.comparison
  // needs pointcloud.toolkit). Callers put the app first, so a link written for
  // this handler still does the obvious thing if it is ever read one-at-a-time.
  const raws = new URLSearchParams(window.location.search).getAll('plugin');
  if (!raws.length) return;
  const registryOrigin = new URL(pluginHost.getRegistryUrl(), window.location.href).origin;
  // Sequential, not Promise.all: installs share the host's registry + install set,
  // and a dependency that lands mid-way through another install is a race nobody
  // wants to debug. Order is as given.
  for (const raw of raws) {
    let url: URL;
    try {
      url = new URL(raw, window.location.href);
    } catch {
      console.warn(`[plugin] ignoring ?plugin=${raw} — not a URL`);
      continue;
    }
    if (url.origin !== window.location.origin && url.origin !== registryOrigin) {
      reportPluginLinkFailure(url.href,
        `refused: only this site (${window.location.origin}) or its registry (${registryOrigin}) `
        + 'may be installed from a link');
      continue;
    }
    try {
      const rec = await pluginHost.installFromManifestUrl(url.href);
      console.info(`[plugin] installed ${rec.id}@${rec.version} for review`);
    } catch (e) {
      // Say it where a reviewer will see it, and keep going: one broken
      // dependency should not cost you the app you actually opened.
      reportPluginLinkFailure(url.href, (e as Error).message);
    }
  }
})();

/** Post a failed review install to the InfoBar (§11), console as the fallback. */
function reportPluginLinkFailure(href: string, message: string): void {
  console.warn(`[plugin] could not install ${href}: ${message}`);
  (app.ui.infobar as { addEntry?: (e: unknown) => void } | undefined)?.addEntry?.({
    source: 'plugin-link',
    title: 'Review link could not install this plugin',
    data: { url: href, reason: message },
  });
}

(window as unknown as { exportAppConfig?: (label?: string) => AppConfig }).exportAppConfig = (label?: string) => {
  const cfg = exportAppConfig(app, label);
  const json = JSON.stringify(cfg, null, 2);
  console.info(json);
  const url = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = 'app-cfg.json';
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return cfg;
};

// Deep-link entry: `…/admin/?checkpoint=<url>` boots the editor straight onto a
// saved engine snapshot (an alert "open result" link, a shared scene). The URL
// is fetched verbatim and applied through the same path as the Load button
// (plugins auto-install). Fallback-safe: with no `?checkpoint=` this is a no-op,
// so a plain load is byte-identical — the standalone/default flow is untouched.
{
  const q = new URLSearchParams(window.location.search);
  const checkpoint = q.get('checkpoint');
  if (checkpoint) {
    // After the plugins this visit installs on its own — an app door's link set,
    // or the full editor's toolset — so the scene's plugin state lands on
    // installed plugins instead of racing their install.
    const loaded = Promise.all([linkPlugins, bootPlugins]).then(() => sidebar.loadSnapshotFromUrl(checkpoint));
    // Headless recalc runner (datahub `checkpoint_recalc` flow): after a load
    // whose serve-side resolver requested a replay, SAVE the recomputed scene
    // back to the same checkpoint — so every later interactive open (editor,
    // dashboard cell) gets fresh results without paying the recompute.
    // Gates, in order: SBASE (a site serves this build — the param is
    // meaningless without the resolver), the explicit param (no interactive
    // visit carries it), and a non-empty exec log (the engine replays BEFORE
    // loadSnapshotFromUrl resolves, so runs prove a replay actually happened —
    // an unmoved source must not churn the file). The saved JSON carries the
    // refreshed InfoBar row and each plugin's recomputed state, which is what
    // makes the recalc durable. document.title is the runner's cheap signal.
    if (SBASE && q.get('recalc-autosave') === '1') {
      void loaded.then(async () => {
        try {
          const runs = app.execLog.all();
          const ids = Object.keys(runs);
          if (ids.length === 0) {
            document.title = 'recalc-noop';
            return;
          }
          // A FAILED replay must not be saved: the first headless run on the
          // box hit an unreachable asset host, flood errored "Pick an asset",
          // and the autosave overwrote a good checkpoint with a scene that had
          // LOST its point cloud. The InfoBar row (with the error) still
          // reaches the hub's alert through the next interactive save or a
          // later successful run; a degraded save reaches every viewer.
          const failed = ids.filter((id) => {
            const last = runs[id][runs[id].length - 1];
            return !!last && last.error !== undefined;
          });
          if (failed.length > 0) {
            console.error(`[admin] recalc-autosave: NOT saving — replay failed for ${failed.join(', ')}`);
            document.title = 'recalc-failed';
            return;
          }
          const target = checkpoint.split('?')[0];
          const fresh = await exportSnapshot(app, pluginRefsForSnapshot) as Record<string, unknown>;
          // The runner is the FULL editor — every entitled plugin installed,
          // every bar in its default state. Exporting that verbatim CLOBBERED
          // the checkpoint's authored UI (an app-narrowed sidebar came back
          // showing everything) and swelled its plugin mark to the whole
          // catalogue. The runner's job is the NUMBERS: keep the fresh
          // extensions/assets/results and the fresh history rows, but carry
          // the operator's `ui` (minus infobar, whose rows must be the
          // replayed ones), `plugins` mark and `app` over from the ORIGINAL
          // file — the runner is not an app door, so it must not unmark one.
          try {
            const orig = await fetch(`${target}?raw=1`).then((r) => (r.ok ? r.json() : null)) as
              Record<string, unknown> | null;
            if (orig) {
              const freshInfobar = (fresh.ui as { infobar?: unknown } | undefined)?.infobar;
              if (orig.ui && typeof orig.ui === 'object') {
                fresh.ui = { ...(orig.ui as object), ...(freshInfobar ? { infobar: freshInfobar } : {}) };
              } else if (freshInfobar) {
                fresh.ui = { infobar: freshInfobar };
              } else {
                delete fresh.ui;
              }
              if (orig.plugins) fresh.plugins = orig.plugins;
              else delete fresh.plugins;
              if (orig.app) fresh.app = orig.app;
              else delete fresh.app;
            }
          } catch { /* preservation is best-effort; the numbers still land */ }
          await putCheckpoint(target, fresh);
          console.info(`[admin] recalc-autosave: saved back to ${target}`);
          document.title = 'recalc-saved';
        } catch (e) {
          console.error('[admin] recalc-autosave failed', e);
          document.title = 'recalc-failed';
        }
      });
    } else {
      void loaded;
    }
  }
}

// App-side WASD + QE fly controls (kept out of the engine library so other
// consumers can pick their own input scheme). Speed auto-scales with orbit
// distance, Shift = boost, Alt = fine. Disposable handle stored on window
// for dev introspection.
const flyKeys = attachFlyKeys(app);

// Expose for console debugging in dev.
(window as unknown as { app?: Viewer; flyKeys?: { dispose: () => void }; pluginHost?: PluginHost }).app = app;
(window as unknown as { flyKeys?: { dispose: () => void } }).flyKeys = flyKeys;
(window as unknown as { pluginHost?: PluginHost }).pluginHost = pluginHost;

// NOTE: We used to auto-load placeholder Potree / IFC / splat assets from
// /public/assets/* on boot for development convenience. That was removed
// because:
//   - the IFC sample loaded automatically every time the page opened, which
//     was confusing and slowed first paint;
//   - users primarily load their own assets via the toolbar or drag-drop;
//   - keeping the dev placeholders meant shipping them in the published
//     bundle (the .ifc / .splat fixtures were shared with users by accident).
// The toolbar's load buttons and the drop-zone are the only entry points now.
